import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { TimesheetsService } from '../api/TimesheetsAPI';
import { ShiftRequestsService } from '../api/ShiftRequestsAPI';
import { useAuth } from '../store';
import { useLightMode } from '../utils';
import { LOCATION_META, CITIES } from '../constants';
import { Header } from '../components/Header/Header';
import { TimesheetDrawer } from '../components/TimesheetDrawer/TimesheetDrawer';
import s from '../styles/staff.module.css';

// The week read as payroll rather than as a calendar: who worked, against what
// was planned, and what needs a look before it is paid.
//
// The figures are live until somebody signs the week off. Approving freezes
// them, and if a clock time is edited afterwards the row says so rather than
// quietly showing the new number — see timesheets.service.

const DAY_MS = 86400000;
const iso = (dt) => new Date(dt).toISOString().slice(0, 10);
const utcDay = (key) => new Date(`${key}T00:00:00Z`);
const addDays = (key, n) => iso(new Date(utcDay(key).getTime() + n * DAY_MS));
const localDay = (v) => {
  const dt = new Date(v);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const weekStartOf = (key) => {
  const dt = utcDay(key);
  dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
  return iso(dt);
};
const dur = (mins) => {
  const n = Math.max(0, Math.round(mins || 0));
  if (!n) return '0h';
  const h = Math.floor(n / 60);
  return h ? `${h}h${n % 60 ? ` ${n % 60}m` : ''}` : `${n}m`;
};
const tm = (v) => (v ? new Date(v).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—');
const dShort = (key) => utcDay(key).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dowOf = (key) => utcDay(key).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
// The grid runs 6 AM to 10 PM, as the schedule's does.
const GRID_FROM = 6 * 60;
const GRID_TO = 22 * 60;
const GRID_SPAN = GRID_TO - GRID_FROM;
const pct = (m) => `${Math.min(100, Math.max(0, ((m - GRID_FROM) / GRID_SPAN) * 100))}%`;
const span = (a, b) => `calc(${pct(b)} - ${pct(a)})`;
const minsIntoDay = (v) => {
  const dt = new Date(v);
  return dt.getHours() * 60 + dt.getMinutes();
};
const dLong = (key) => utcDay(key).toLocaleDateString('en-US',
  { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });

const initials = (n) => String(n || '').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

// "Clock out 3:02 PM → 5:04 PM · +2h 2m worked" — which end moved, from what
// to what, and what it does to the hours. A request that only says "5:04 PM"
// asks somebody to approve a number with nothing to weigh it against.
function describeFix(r) {
  const parts = [];
  let minutes = r.shiftMinutes || 0;
  if (r.proposedClockInAt) {
    parts.push(`Clock in ${tm(r.shiftClockInAt)} → ${tm(r.proposedClockInAt)}`);
  }
  if (r.proposedClockOutAt) {
    parts.push(`Clock out ${tm(r.shiftClockOutAt)} → ${tm(r.proposedClockOutAt)}`);
  }
  const from = new Date(r.proposedClockInAt || r.shiftClockInAt);
  const to = r.proposedClockOutAt ? new Date(r.proposedClockOutAt)
    : (r.shiftClockOutAt ? new Date(r.shiftClockOutAt) : null);
  let delta = null;
  if (to && !Number.isNaN(from.getTime())) {
    const next = Math.max(0, Math.round((to - from) / 60000));
    const diff = next - minutes;
    if (diff) delta = `${diff > 0 ? '+' : '−'}${dur(Math.abs(diff))} worked`;
  }
  return { text: parts.join(' · '), delta };
}

export function TimesheetsPage() {
  const [lightMode, toggleLightMode] = useLightMode();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const today = localDay(new Date());
  const [week, setWeek] = useState(weekStartOf(today));
  const [city, setCity] = useState('');
  const [spot, setSpot] = useState('');
  const [view, setView] = useState('week');
  const [day, setDay] = useState(localDay(new Date()));
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const weekEnd = addDays(week, 6);

  const { data, isLoading } = useQuery({
    queryKey: ['timesheets', week, spot],
    queryFn: () => TimesheetsService.week(week, weekEnd, spot || undefined),
    enabled: isAdmin,
    placeholderData: (prev) => prev,
  });

  // Clock fixes waiting on a decision. They belong here rather than only on
  // the schedule: a timesheet cannot be signed off while somebody is still
  // asking for the hours in it to be changed.
  const { data: dayData } = useQuery({
    queryKey: ['timesheet-day', day, spot],
    queryFn: () => TimesheetsService.day(day, spot || undefined),
    enabled: isAdmin && view === 'day',
    placeholderData: (prev) => prev,
  });

  const { data: fixData } = useQuery({
    queryKey: ['crew-requests', 'pending'],
    queryFn: () => ShiftRequestsService.list({ status: 'pending' }),
    enabled: isAdmin,
  });
  const fixes = (fixData?.requests || []).filter(r => r.kind === 'clock_fix');

  const { data: detail } = useQuery({
    queryKey: ['timesheet-detail', open, week],
    queryFn: () => TimesheetsService.detail(open, week, weekEnd),
    enabled: isAdmin && !!open,
  });

  // The city filter narrows which garages count, and a person is in if they
  // spent any of the week at one of them.
  // Same two steps as the schedule: a city narrows to its garages, and a
  // garage chip narrows to one. A person counts if they spent any of the week
  // at one of the garages in view.
  const inCity = useMemo(
    () => Object.entries(LOCATION_META)
      .filter(([, m]) => !city || m.city === city)
      .map(([id, meta]) => ({ id, ...meta })),
    [city]);

  // A city covers more than one garage, so that narrowing still happens here;
  // a single garage is pushed to the server, which can scope the hours as well
  // as the list.
  const shown = useMemo(() => {
    if (!city || spot) return null;
    return new Set(inCity.map(l => l.id));
  }, [city, spot, inCity]);

  const pickCity = (next) => { setCity(next); setSpot(''); };

  const people = useMemo(() => (data?.people || [])
    .filter(p => !shown || p.locations.some(l => shown.has(l))), [data, shown]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(week, i)), [week]);
  // Whoever was clicked, from either list. A city filter can hide somebody
  // from the week table while they are still a column in the day grid, and a
  // header that opens nothing is worse than one that opens an unfiltered
  // panel — so the day list stands in, with the week's own figures totalled
  // from the detail the panel is given.
  const person = useMemo(() => {
    if (!open) return null;
    const inWeek = people.find(p => p.idStaff === open);
    if (inWeek) return inWeek;
    const inDay = (dayData?.people || []).find(p => p.idStaff === open);
    if (!inDay) return null;
    const days = detail?.days || [];
    return {
      ...inDay,
      locationNames: (inDay.locations || []).map(id => LOCATION_META[id]?.name || id),
      workedMinutes: days.reduce((n, d) => n + d.workedMinutes, 0),
      scheduledMinutes: days.reduce((n, d) => n + d.scheduledMinutes, 0),
      exceptionCount: 0,
      approved: null,
    };
  }, [people, dayData, detail, open]);

  const totals = useMemo(() => ({
    worked: people.reduce((n, p) => n + p.workedMinutes, 0),
    scheduled: people.reduce((n, p) => n + p.scheduledMinutes, 0),
    onNow: people.filter(p => p.onTheClock),
    exceptions: people.reduce((n, p) => n + p.exceptionCount, 0),
  }), [people]);

  const stepDay = (n) => {
    const next = addDays(day, n);
    setDay(next);
    if (weekStartOf(next) !== week) setWeek(weekStartOf(next));
  };

  // The top arrows move the week. In the day view the day travels with it, to
  // the same weekday — otherwise the card would be showing a day that is no
  // longer in the week the rest of the page is reporting on.
  const stepWeek = (n) => {
    setWeek(addDays(week, n * 7));
    if (view === 'day') setDay(addDays(day, n * 7));
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    queryClient.invalidateQueries({ queryKey: ['timesheet-detail'] });
    queryClient.invalidateQueries({ queryKey: ['timesheet-day'] });
    queryClient.invalidateQueries({ queryKey: ['crew-requests'] });
    // An approved fix rewrites the timecard the crew member is looking at.
    queryClient.invalidateQueries({ queryKey: ['my-shifts'] });
  };

  const run = async (fn, okMsg) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const out = await fn();
      if (okMsg) setNotice(typeof okMsg === 'function' ? okMsg(out) : okMsg);
      refresh();
      return out;
    } catch (err) {
      setError(err?.error || err?.message || 'That did not go through.');
      return null;
    } finally { setBusy(false); }
  };

  const kpis = [
    { k: 'Worked', v: dur(totals.worked), note: `of ${dur(totals.scheduled)} scheduled` },
    { k: 'Scheduled', v: dur(totals.scheduled), note: 'this week', tone: 'attnToneGray' },
    { k: 'On shift now', v: String(totals.onNow.length),
      tone: totals.onNow.length ? 'attnToneGray' : '',
      note: totals.onNow.length
        ? totals.onNow.map(p => p.name.split(' ')[0]).join(', ')
        : 'nobody clocked in' },
    { k: 'To check', v: String(totals.exceptions),
      tone: totals.exceptions ? 'attnToneDue' : '',
      note: 'missed, late or waiting on a fix' },
  ];

  if (!isAdmin) {
    return (
      <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
        <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />
        <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
          <div className={s.attnEmpty}>
            Timesheets show what everybody earned, so they are admin only.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode} />

      <div className={s.shiftPage}>
        <div className={s.schedHead}>
          <div>
            <div className={s.shiftEyebrow}>Timesheets</div>
            <h1 className={`${s.shiftToday} ${s.schedTitle}`}>
              {view === 'day'
                ? dLong(day)
                : `${dShort(week)} – ${dShort(weekEnd)}, ${weekEnd.slice(0, 4)}`}
            </h1>
          </div>
          <div className={s.schedActions}>
            <button className={s.schedPost} disabled={busy || !people.length}
                    onClick={() => run(
                      () => TimesheetsService.approveAll(week, weekEnd, spot || undefined),
                      (r) => `Signed off ${r.approved} timesheet${r.approved === 1 ? '' : 's'}.`)}>
              Approve the week
            </button>
          </div>
        </div>

        <div className={s.schedNav}>
          <button className={s.calNavBtn} aria-label="Previous week"
                  onClick={() => stepWeek(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.75" strokeLinecap="square"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <button className={s.calNavBtn} aria-label="Next week"
                  onClick={() => stepWeek(1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.75" strokeLinecap="square"><path d="M9 6l6 6-6 6" /></svg>
          </button>
          {week !== weekStartOf(today) && (
            <button className={s.calToday}
                    onClick={() => { setWeek(weekStartOf(today)); setDay(today); }}>This week</button>
          )}
          <div className={s.calSeg}>
            <button onClick={() => pickCity('')} className={`${s.calSegBtn} ${!city ? s.calSegOn : ''}`}>All</button>
            {CITIES.map(c => (
              <button key={c} onClick={() => pickCity(c)}
                      className={`${s.calSegBtn} ${city === c ? s.calSegOn : ''}`}>{c}</button>
            ))}
          </div>

          {city && inCity.length > 1 && (
            <div className={s.schedSpots}>
              <button onClick={() => setSpot('')}
                      className={`${s.schedSpot} ${!spot ? s.schedSpotOn : ''}`}>
                {inCity.length === 2 ? 'Both garages' : 'All garages'}
              </button>
              {inCity.map(l => (
                <button key={l.id} onClick={() => setSpot(l.id)}
                        className={`${s.schedSpot} ${spot === l.id ? s.schedSpotOn : ''}`}>
                  {l.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && <div className={s.attnError}>{error}</div>}
        {notice && <div className={s.schedNotice}>{notice}</div>}

        {fixes.length > 0 && (
          <div className={s.schedBanner}>
            <span className={s.schedBannerDot} />
            <div className={s.schedBannerBody}>
              <div className={s.schedBannerTitle}>
                {fixes.length} clock time fix{fixes.length === 1 ? '' : 'es'} waiting
              </div>
              {fixes.map(r => {
                const f = describeFix(r);
                return (
                  <div key={r.idRequest} className={s.tsFixRow}>
                    <div className={s.tsFixText}>
                      <strong className={s.tsFixWho}>{r.staffName || '—'}</strong>
                      {r.shiftClockInAt ? ` · ${dowOf(localDay(r.shiftClockInAt))} `
                        + `${dShort(localDay(r.shiftClockInAt))}` : ''}
                      {f.text ? ` · ${f.text}` : ''}
                      {r.reason ? ` · ${r.reason}` : ''}
                      {f.delta ? ` · ${f.delta}` : ''}
                    </div>
                    <div className={s.schedDecide}>
                      <button className={s.dayAction} disabled={busy}
                              onClick={() => run(() => ShiftRequestsService.decline(r.idRequest), 'Declined.')}>
                        Decline
                      </button>
                      <button className={s.schedPost} disabled={busy}
                              onClick={() => run(() => ShiftRequestsService.approve(r.idRequest), 'Approved — the timecard is updated.')}>
                        Approve
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className={s.schedStats}>
          {kpis.map(x => (
            <div key={x.k} className={s.schedStat}>
              <div className={s.schedStatLabel}>{x.k}</div>
              <div className={`${s.schedStatValue} ${x.tone ? s[x.tone] : ''}`}>{x.v}</div>
              <div className={s.schedStatNote}>{x.note}</div>
            </div>
          ))}
        </div>

        <div className={s.schedWrapHead}>
          {/* The day steps from the card it belongs to, so the day in view and
              the control that changes it are the same thing to look at. */}
          {view === 'day' && (
            <>
              <button className={s.calNavBtn} aria-label="Previous day" onClick={() => stepDay(-1)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.75" strokeLinecap="square"><path d="M15 6l-6 6 6 6" /></svg>
              </button>
              <button className={s.calNavBtn} aria-label="Next day" onClick={() => stepDay(1)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.75" strokeLinecap="square"><path d="M9 6l6 6-6 6" /></svg>
              </button>
            </>
          )}
          <div className={s.schedWrapWho}>
            <span className={s.schedWrapTitle}>
              {view === 'day' ? dLong(day) : 'Crew this week'}
            </span>
            <span className={s.schedWrapSub}>
              {view === 'day'
                ? `${dayData?.totals?.crew || 0} crew · ${dur(dayData?.totals?.workedMinutes)} clocked`
                : `${people.length} crew · ${dur(totals.worked)} of ${dur(totals.scheduled)}`}
            </span>
          </div>
          <div className={s.calSeg} style={{ marginLeft: 'auto' }}>
            {['week', 'day'].map(v => (
              <button key={v} onClick={() => setView(v)}
                      className={`${s.calSegBtn} ${view === v ? s.calSegOn : ''}`}>
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* One day, a column per technician: what was planned behind what was
            actually clocked, so a gap between them is visible rather than
            inferred from two numbers. */}
        {view === 'day' && (
          <div className={s.schedWrap}>
            {!(dayData?.people || []).length && (
              <div className={s.railEmpty}>Nobody is scheduled or clocked in on this day.</div>
            )}
            {(dayData?.people || []).length > 0 && (
              <div className={s.schedDayGrid}
                   style={{ gridTemplateColumns: `52px repeat(${dayData.people.length}, minmax(120px, 1fr))` }}>
                <div className={s.schedDayGutter}>
                  {Array.from({ length: GRID_SPAN / 60 + 1 }, (_, i) => {
                    const h = GRID_FROM / 60 + i;
                    return (
                      <div key={h} className={s.calHour}>
                        {new Date(Date.UTC(2020, 0, 1, h)).toLocaleTimeString('en-US',
                          { hour: 'numeric', timeZone: 'UTC' })}
                      </div>
                    );
                  })}
                </div>

                {dayData.people.map(p => (
                  <div key={p.idStaff} className={s.schedDayCol}>
                    {/* The same panel the week table opens, so a column that
                        looks wrong can be read where it is noticed. */}
                    <span className={`${s.schedDayHeadCell} ${s.tsHeadClick}`}
                          role="button" tabIndex={0}
                          title={`${p.name} — clock activity`}
                          onClick={() => setOpen(p.idStaff)}
                          onKeyDown={(e) => { if (e.key === 'Enter') setOpen(p.idStaff); }}>
                      <span className={s.schedDayWho}>
                        {p.name.split(' ')[0].toUpperCase()}
                      </span>
                      <span className={s.schedDayHours}>
                        {p.workedMinutes ? dur(p.workedMinutes) : '—'}
                      </span>
                      <span className={`${s.tsDayState} ${
                        p.onTheClock ? s.attnToneGray
                          : p.missingClockOut ? s.attnToneDue
                          : p.workedMinutes ? '' : s.attnToneDue}`}>
                        {p.onTheClock ? 'On shift'
                          : p.missingClockOut ? 'Missing clock-out'
                          : p.workedMinutes ? 'Clocked out' : 'Not clocked in'}
                      </span>
                    </span>

                    <span className={s.schedDayBody}>
                      {p.scheduled.map(x => (
                        <span key={x.idSchedule} className={s.tsPlanBlock}
                              style={{ top: pct(minsIntoDay(x.startAt)),
                                       height: span(minsIntoDay(x.startAt), minsIntoDay(x.endAt)) }}>
                          <span className={s.calBlockTime}>{tm(x.startAt)} – {tm(x.endAt)}</span>
                          <span className={s.calBlockWhere}>{x.locationName}</span>
                        </span>
                      ))}
                      {p.worked.map(w => (
                        <span key={w.idShift}
                              className={`${s.tsClockBar} ${w.open && p.missingClockOut ? s.tsClockBarBad : ''}`}
                              style={{ top: pct(minsIntoDay(w.clockInAt)),
                                       height: span(minsIntoDay(w.clockInAt),
                                         w.clockOutAt ? minsIntoDay(w.clockOutAt) : minsIntoDay(new Date())) }} />
                      ))}
                      {day === localDay(new Date()) && (
                        <span className={s.calNow} style={{ top: pct(minsIntoDay(new Date())) }} />
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className={s.schedLegend} style={{ padding: '0 14px 12px' }}>
              <span><i className={s.legendTsPlan} />Scheduled</span>
              <span><i className={s.legendTsClock} />Clocked</span>
              <span><i className={s.legendTsBad} />Missing clock-out</span>
              <span><i className={s.legendNow} />Now</span>
            </div>
          </div>
        )}

        {view === 'week' && (
        <div className={s.tableWrap} style={{ marginTop: 0 }}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Crew</th><th>Location</th><th className={s.num}>Shifts</th>
                <th className={s.num}>Scheduled</th><th className={s.num}>Worked</th>
                <th>Exceptions</th><th>Right now</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className={s.loading}>Loading…</td></tr>}
              {!isLoading && !people.length && (
                <tr><td colSpan={7} className={s.empty}>Nobody worked or was scheduled this week.</td></tr>
              )}
              {people.map(p => (
                <tr key={p.idStaff} className={s.tsRowClick}
                    onClick={() => setOpen(p.idStaff)}
                    title={`${p.name} — clock activity`}>
                  <td className={s.schedReqCrew}>
                    <span className={s.schedAvatar}>{initials(p.name)}</span>
                    <strong className={s.schedCrewName} title={p.name}>{p.name}</strong>
                  </td>
                  <td>{p.locationNames.join(' & ') || '—'}</td>
                  <td className={s.num}>{p.shifts}</td>
                  <td className={`${s.num} ${s.attnToneGray}`}>{dur(p.scheduledMinutes)}</td>
                  <td className={s.num}><strong>{dur(p.workedMinutes)}</strong></td>
                  <td className={s.cellWrap}>
                    {p.exceptionCount
                      ? <span className={s.attnToneDue}>
                          {[...new Set(p.exceptions.map(e => e.text))].join(' · ')}
                        </span>
                      : <span className={s.attnToneGray}>none</span>}
                  </td>
                  <td>
                    {p.onTheClock
                      ? <span className={`${s.statusPill} ${s.attnToneGray}`}>on shift</span>
                      : p.approved
                        ? <span className={`${s.statusPill} ${p.approved.drifted ? s.attnToneWarn : s.attnToneGray}`}>
                            {p.approved.drifted ? 'changed since' : 'approved'}
                          </span>
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        <div className={s.schedLegend}>
          <span className={s.schedLegendHint}>
            Tap a person to see their clock times. Approving freezes the week's figures for payroll.
          </span>
        </div>

        {/* Inside the page wrapper rather than portalled to the body, so the
            panel is a descendant of `.lightPage` and follows the theme. */}
        {person && detail && (
          <TimesheetDrawer
            person={person}
            detail={detail}
            weekDays={weekDays}
            range={{ start: week, end: weekEnd }}
            busy={busy}
            onClose={() => setOpen(null)}
            onChanged={refresh}
            onApprove={() => run(
              () => TimesheetsService.approve(person.idStaff, week, weekEnd), 'Signed off.')}
            onReopen={() => run(
              () => TimesheetsService.unapprove(person.idStaff, week), 'Reopened.')}
          />
        )}
      </div>
    </div>
  );
}
